<template>
  <div class="password-strength">
    <div class="progress">
      <div
        class="progressbar"
        :class="{ [strengthClass]: true }" 
        role="progressbar" 
        aria-valuenow="40" 
        aria-valuemin="0" 
        aria-valuemax="100" 
        :style="{ width: percentage + '%' }" 
      />
    </div>
    
    <ul 
      v-if="password && percentage < 100" 
      class="password-strength-message"
    >
      <li 
        v-for="(rule, index) in messages"
        :key="index"
        class="password-strength-message__item"
        :class="{ 'text-success': rule.valid, 'text-danger': !rule.valid }"
      >
        <i 
          :class="{'checkmark': rule.valid, 'closemark': !rule.valid}"
        /> {{ rule.message }}
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  props: {
    password: {
      type: String,
      default: ''
    },
    rules: {
      type: Array,
      default: () => [
        { 
          name: 'uppercase', 
          regex: /[A-Z]/, 
          message: 'One uppercase character' 
        },
        { 
          name: 'lowercase', 
          regex: /[a-z]/, 
          message: 'One lowercase character' 
        },
        { 
          name: 'special', 
          regex: /[!@#$%^&*()_+[\]{};':"\\|,.<>/?]/, 
          message: 'One special character' 
        },
        {
          name: 'number', 
          regex: /\d/, 
          message: 'One number' 
        },
        { 
          name: 'length', 
          regex: /.{8,}/, 
          message: '8 characters minimum' 
        }
      ]
    }
  },

  emits: ['password-strength'],

  data() {
    return {
      percentage: 0,
      strengthClass: ''
    }
  },

  computed: {
    messages() { 
      let messages = [];

      this.rules.forEach((rule) => {
        messages.push({
          name: rule.name,
          message: rule.message,
          valid: rule.regex.test(this.password)
        });
      });

      return messages;
    },
    strength() {
      let strength = 0;

      this.messages.forEach((message) => {
        if (message.valid) {
          strength++;
        }
      });

      return strength;
    }
  },

  watch: {
    password() { 
      this.percentage = (this.strength / this.rules.length) * 100;

      this.$emit('password-strength', this.percentage);

      if (this.percentage >= 100) {
        this.strengthClass = 'success';
      } else if (this.percentage >= 75) {
        this.strengthClass = 'fair';
      } else if (this.percentage >= 50) {
        this.strengthClass = 'warning';
      } else {
        this.strengthClass = 'danger';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$green: #02b502;
$red: #e90f10;

.password-strength-message {
  margin-top: 10px;
  margin-bottom: 10px;
  padding-left: 0;
  list-style: none;

  display: flex;
  flex-wrap:wrap;

  .password-strength-message__item {
    flex-basis:100%;  
    position: relative;
  }

  .checkmark {
    box-sizing: border-box;
    position: relative;
    display: inline-block;
    transform: scale(var(--ggs,1));
    width: 22px;
    height: 22px;
    border: 2px solid transparent;
    border-radius: 100px
  }
  .checkmark::after {
    content: "";
    display: block;
    box-sizing: border-box;
    position: absolute;
    left: 3px;
    top: 0.3rem;
    width: 6px;
    height: 10px;
    border-width: 0 2px 2px 0;
    border-style: solid;
    transform-origin: bottom left;
    transform: rotate(45deg)
  }

  .closemark {
    box-sizing: border-box;
    position: relative;
    display: inline-block;
    transform: scale(var(--ggs,1));
    width: 22px;
    height: 22px;
    border: 2px solid transparent;
    border-radius: 40px
  }
  .closemark::after,
  .closemark::before {
    content: "";
    display: block;
    box-sizing: border-box;
    position: absolute;
    width: 16px;
    height: 2px;
    background: currentColor;
    transform: rotate(45deg);
    border-radius: 5px;
    top: 0.83rem;
    left: 1px
  }
  .closemark::after {
    transform: rotate(-45deg)
  }
}

@media (min-width: 768px) {
  .password-strength-message__item {
    flex-basis:50% !important;
  }
}

.password-strength 
{
  margin-top: -1.1rem;
  .progress {
    height: 3px !important;
    background-color: #e0e0e0;
  }

  .progressbar {
    height: 3px !important;
    background-color: $green;
  }

  .danger {
    background-color: $red;
  }

  .warning {
    background-color: #ffad00;
  }

  .success {
    background-color: $green;
  }
  .fair {
    background-color: #799c79;
  }

  .text-success {
    color: #02b502;
  }

  .text-danger {
    color: $red;
  }
}
</style>